/**
 * Merge and catch-up functions that make a poll tick, and a return from
 * background, both gapless and duplicate-free (ISSUE-280 plan §5.7).
 */

// Server-resolved shapes for the three kinds ISSUE-282 renders, read field
// for field off `/message/list`'s response (`postMessageList.ts`). Kept
// here rather than re-derived in a bubble component, per
// `server-resolves-client-renders.mdc`.
export type typeMessagePollOption = {
  optionId: string;
  label: string;
  voteCount: number;
};

export type typeMessagePoll = {
  question: string;
  allowMultiple: boolean;
  options: typeMessagePollOption[];
  myVoteOptionIds: string[];
  totalVotes: number;
};

export type typeMessageGameCardTeam = {
  teamId: string;
  name: string;
  abbreviation: string;
  score?: number | null;
};

export type typeMessageGameCard = {
  eventId: string;
  leagueName: string;
  statusLabel: string;
  /** ISSUE-370 R6. Optional because three existing test fixtures build this
   *  type by hand; `/message/list` always returns it on a real gameCard. */
  kickoffAtIso?: string | null;
  home: typeMessageGameCardTeam;
  away: typeMessageGameCardTeam;
};

// ISSUE-750. fileName is always a legible string (messaging-api substitutes
// the PDF_FILE_NAME_FALLBACK stand-in server side for a name that was never
// captured), never empty, never null. byteSize is null whenever media-api
// could not resolve one (an outage, or the object not yet published).
export type typeMessagePdf = {
  fileName: string;
  byteSize: number | null;
};

/**
 * ISSUE-1360. The card messaging-api's `messaging.unfurlLink` job stores for
 * the first https link in a text message. No image on purpose: hotlinking
 * would hand the fan's IP to the site (messaging overview, "Link cards").
 */
export type typeMessageLinkPreview = {
  url: string;
  title: string;
  description: string | null;
  siteName: string | null;
  hostname: string;
};

export type typeMessageRow = {
  messageId: string;
  senderId: string;
  senderDisplayName: string;
  senderAvatarUrl: string | null;
  kind:
    | "text"
    | "image"
    | "gif"
    | "poll"
    | "gameCard"
    | "systemAnnouncement"
    // ISSUE-750. Nothing draws it yet, but dropping it here is what let the
    // pane relabel a PDF as text before it ever reached a bubble.
    | "pdf";
  body: string | null;
  /** Populated only for `kind: "image"` (R2). Order preserved from the
   *  server, which preserves the order the fan attached them in. */
  mediaObjectIds: string[] | null;
  /** Populated only for `kind: "poll"` (R5-R7). */
  poll: typeMessagePoll | null;
  /** Populated only for `kind: "gameCard"` (R8). */
  gameCard: typeMessageGameCard | null;
  /** Populated only for `kind: "pdf"` (ISSUE-750 R2). */
  pdf: typeMessagePdf | null;
  /** Populated only for `kind: "gif"` (R3). The media id media-api handed
   *  back from `/gif/select`, resolved to a URL through `/object/get-batch`
   *  exactly as a photo is. Before this field existed a gif row arrived with
   *  its payload dropped and fell through to the empty text bubble. */
  gifObjectId: string | null;
  replyToMessageId: string | null;
  replyToPreview: string | null;
  isEdited: boolean;
  isPinned: boolean;
  isDeleted: boolean;
  sentAtIso: string;
  editedAtIso: string | null;
  announcementKind: string | null;
  reactions: { emoji: string; count: number; reactedByMe: boolean }[];
  /** ISSUE-1360. Null until the unfurl job fills it, and on any miss. */
  linkPreview: typeMessageLinkPreview | null;
};

export const MAX_CATCHUP_PAGES = 10;

/**
 * The id prefix for a row this browser composed that the server has never seen
 * (ISSUE-842).
 *
 * `ConversationPane` prepends such a row the moment the fan hits send, so it
 * sits at index 0 until the send lands, and it carries an id that no
 * `/message/list` page can ever contain.
 *
 * Every function below has to know that. Without it the merge reads the
 * optimistic row as "the newest message I know about", asks the server to find
 * it, never finds it, concludes local state is hopelessly behind, and hard
 * resyncs, throwing away the one row on screen the server cannot re-supply.
 * That is how a send that failed took the fan's text with it about five
 * seconds later.
 */
export const OPTIMISTIC_ID_PREFIX = "pending-";

/** The id the pane gives a row it has not yet had accepted by the server. */
export function optimisticMessageId(requestId: string): string {
  return `${OPTIMISTIC_ID_PREFIX}${requestId}`;
}

export function isOptimisticId(messageId: string): boolean {
  return messageId.startsWith(OPTIMISTIC_ID_PREFIX);
}

/** Splits held rows into the ones only this browser knows about and the ones the server has
 *  confirmed. Only the confirmed half can be compared against a server page; the optimistic half
 *  is carried through untouched. */
function partitionOptimistic(rows: readonly typeMessageRow[]): {
  optimistic: typeMessageRow[];
  fromServer: typeMessageRow[];
} {
  return {
    optimistic: rows.filter((row) => isOptimisticId(row.messageId)),
    fromServer: rows.filter((row) => !isOptimisticId(row.messageId)),
  };
}

/** Fresh page always wins for any messageId it contains (picks up edits/deletes/reactions on
 *  already-known messages); anything only in `existing` (older than the fresh page's reach) is
 *  kept, appended after, in its existing order.
 *
 *  Optimistic rows are the exception and lead the result. The list is newest first, and a row the
 *  fan typed a moment ago is newer than anything the server can return. Appending it after the
 *  fresh page would leave the fan's own failed message sorted as the oldest thing in the
 *  conversation, scrolled away from the composer they are looking at (ISSUE-842). */
export function mergeFreshPage(input: {
  existing: readonly typeMessageRow[];
  freshFirstPage: readonly typeMessageRow[];
}): typeMessageRow[] {
  const freshIds = new Set(input.freshFirstPage.map((row) => row.messageId));
  const { optimistic, fromServer } = partitionOptimistic(input.existing);
  const remainder = fromServer.filter((row) => !freshIds.has(row.messageId));
  return [...optimistic, ...input.freshFirstPage, ...remainder];
}

/** Appends an explicitly-requested older page, de-duplicated against what is already held. */
export function appendOlderPage(input: {
  existing: readonly typeMessageRow[];
  olderPage: readonly typeMessageRow[];
}): typeMessageRow[] {
  const existingIds = new Set(input.existing.map((row) => row.messageId));
  const newOnes = input.olderPage.filter(
    (row) => !existingIds.has(row.messageId),
  );
  return [...input.existing, ...newOnes];
}

/** True when `existing` holds at least one server-confirmed row and its newest-known messageId does
 *  not appear anywhere in the fresh first page. This is the only condition under which a plain
 *  `mergeFreshPage` would silently drop messages that arrived while the client was away longer than
 *  one page's worth.
 *
 *  "Newest known" means newest known TO THE SERVER, so optimistic rows are looked past rather than
 *  asked about. A held list of nothing but optimistic rows is not a gap either, for the same reason
 *  an empty one is not: there is no confirmed ground for the fresh page to be disconnected from.
 *
 *  Before ISSUE-842 this read `existing[0]`, which after any send at all is the optimistic row. That
 *  made this return true on every poll following every send, so the ordinary path through a healthy
 *  conversation was the expensive one: a catch-up walk hunting an id the server could not have. */
export function hasCatchUpGap(input: {
  existing: readonly typeMessageRow[];
  freshFirstPage: readonly typeMessageRow[];
}): boolean {
  const newest = partitionOptimistic(input.existing).fromServer[0];
  if (newest === undefined) return false;
  return !input.freshFirstPage.some(
    (row) => row.messageId === newest.messageId,
  );
}

/** Walks `nextCursor` backwards from the fresh page, accumulating pages, until either the
 *  previously-known newest messageId is found in a page, or MAX_CATCHUP_PAGES is reached, or
 *  `nextCursor` becomes null. `fetchPage` is injected so this function is a pure orchestration
 *  over a caller-supplied network call and is unit-testable with a fake. */
export async function catchUpMessages(input: {
  existing: readonly typeMessageRow[];
  freshFirstPage: readonly typeMessageRow[];
  freshNextCursor: string | null;
  fetchPage: (
    cursor: string,
  ) => Promise<{ items: typeMessageRow[]; nextCursor: string | null }>;
}): Promise<typeMessageRow[]> {
  // Optimistic rows are held aside first and put back at the head of every
  // branch below: they are the newest thing on screen and the only thing here
  // the server cannot re-supply (ISSUE-842).
  const { optimistic, fromServer } = partitionOptimistic(input.existing);
  const newest = fromServer[0];
  if (newest === undefined) {
    return [...optimistic, ...input.freshFirstPage];
  }

  const newestKnownId = newest.messageId;
  let accumulated: typeMessageRow[] = [...input.freshFirstPage];
  let cursor = input.freshNextCursor;
  let pagesWalked = 0;
  let found = false;

  while (!found && cursor !== null && pagesWalked < MAX_CATCHUP_PAGES) {
    const page = await input.fetchPage(cursor);
    accumulated = [...accumulated, ...page.items];
    found = page.items.some((row) => row.messageId === newestKnownId);
    cursor = page.nextCursor;
    pagesWalked += 1;
  }

  if (found) {
    const accumulatedIds = new Set(accumulated.map((row) => row.messageId));
    const remainder = fromServer.filter(
      (row) => !accumulatedIds.has(row.messageId),
    );
    return [...optimistic, ...accumulated, ...remainder];
  }

  // The cap was hit, or the cursor ran out: a bounded hard resync rather than
  // an unbounded fetch loop.
  //
  // Optimistic rows are exempt from that resync (ISSUE-842). Everything else
  // dropped here can be fetched again; a row the server never accepted cannot,
  // so discarding it is not a resync, it is deleting what the fan wrote.
  return [...optimistic, ...accumulated];
}
